import React, { FC, ReactElement, memo, useCallback, useMemo, useState } from 'react';
import { Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { ModernSelect as Select } from 'shared/ui/ModernSelect';
import { Checkbox } from 'shared/ui/Checkbox';
import { ModernDatePicker as DatePicker } from 'shared/ui/ModernDatePicker';
import { useCloseContractMutation, CloseContractParams, Contract } from 'entities/Contract';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import dayjs, { Dayjs } from 'dayjs';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { getServerDateFormat } from 'shared/utils/helpers/getDateFormat';
import { UserPermissions } from 'entities/User';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { EmailType } from 'entities/Email';

interface CloseContractProps {
  contract: Contract;
  action: ReactElement;
}

export const CloseContract: FC<CloseContractProps> = memo((props) => {
  const { contract, action } = props;

  const [form] = Form.useForm();

  const [showExtendedOptions, setShowExtendedOptions] = useState(false);
  const [isOpenedModal, setOpenedModal] = useState(false);

  const isForbidden = useCheckPermission(UserPermissions.CLOSE_CONTRACT_EXTENDED);

  const { t } = useAppTranslation(['contracts', 'common']);

  const { isDesktop } = useScreenBreakpoints();

  const [closeContract, { isLoading }] = useCloseContractMutation();

  const openModal = useCallback((): void => {
    setOpenedModal(true);
  }, []);

  const refundOptions = useMemo(
    () => [
      { label: t('Default'), value: 'default' },
      { label: t('Refund all'), value: 'refund-all' },
      { label: t('Disable refunds'), value: 'no-refunds' },
    ],
    [t],
  );

  const emailTypeOptions = useMemo(
    () => [
      { label: t('Default'), value: EmailType.CLOSE_CONTRACT },
      { label: t('Debtor email'), value: EmailType.DEBTOR_CLOSE_CONTRACT },
    ],
    [t],
  );

  const defaultValues = {
    refundOptions: 'default',
    leaveDate: dayjs().startOf('day'),
    ignoreMinContractDuration: false,
    enableDepositCharge: true,
    enableUserBalanceCharge: false,
    issueUnpaidInvoices: true,
    emailType: EmailType.CLOSE_CONTRACT,
  };

  const disableDate = useCallback(
    (currentDate: Dayjs): boolean => {
      return !isForbidden
        ? currentDate.isBefore(contract.createdAt, 'day')
        : currentDate.isBefore(dayjs(contract.initialEndDate).subtract(1, 'day'), 'day');
    },
    [contract.createdAt, contract.initialEndDate, isForbidden],
  );

  const toggleExtendedOptions = (): void => {
    setShowExtendedOptions((prev) => !prev);
  };

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
    form.resetFields();
  }, [form]);

  const handleCloseContract = useCallback(
    async (data: CloseContractParams): Promise<void> => {
      try {
        if (contract) {
          await closeContract({
            contractId: contract.contractId,
            params: {
              ...data,
              leaveDate: getServerDateFormat(data.leaveDate),
            },
          }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Contract closed successfully', { ns: 'common' }));
          closeModal();
        }
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when closing contract', { ns: 'common' }));
        console.log('error', error);
      } finally {
        closeModal();
      }
    },
    [contract, closeContract, t, closeModal],
  );

  return (
    <>
      {React.cloneElement(action, { onClick: openModal })}

      <Modal width={isDesktop ? 400 : 350} isOpen={isOpenedModal} onClose={closeModal}>
        <Form form={form} layout="vertical" initialValues={defaultValues} onFinish={handleCloseContract} className="flex flex-col">
          <div className="font-semibold text-xl mb-3 font-tektur font-tektur">{t('Close contract options')}</div>
          <div className="text-primaryLight font-normal">{t('Are you sure you want to close this contract?')}</div>

          <div className="mt-5">
            <Form.Item
              className="w-full"
              label={t('Close date')}
              name="leaveDate"
              rules={[
                {
                  required: true,
                  message: t('Please enter leave date!'),
                },
              ]}
            >
              <DatePicker
                className="h-[52px] w-full"
                format={CLIENT_DATE_FORMAT}
                placeholder={t('Enter leave date')}
                disabledDate={disableDate}
                inputReadOnly
                defaultPickerValue={dayjs().startOf('day')}
              />
            </Form.Item>

            <Form.Item
              label={t('Email type')}
              name="emailType"
              className="flex-1"
              rules={[
                {
                  required: true,
                  message: t('Please select email type!'),
                },
              ]}
            >
              <Select options={emailTypeOptions} placeholder={t('Please, select email type')} bordered />
            </Form.Item>
          </div>

          <div className="mb-5">
            <Form.Item label={t('Refund options')} name="refundOptions" className="flex-1">
              <Select options={refundOptions} placeholder={t('Please, select warehouse')} bordered disabled={isForbidden} />
            </Form.Item>
            {showExtendedOptions && (
              <>
                <Form.Item name="issueUnpaidInvoices" valuePropName="checked" className="mb-3">
                  <Checkbox defaultChecked name="issueUnpaidInvoices" disabled={isForbidden}>
                    {t('Issue unpaid invoices')}
                  </Checkbox>
                </Form.Item>
                <Form.Item name="enableDepositCharge" valuePropName="checked" className="mb-3">
                  <Checkbox defaultChecked name="enableDepositCharge" disabled={isForbidden}>
                    {t('Enable deposit charge')}
                  </Checkbox>
                </Form.Item>
                <Form.Item name="enableUserBalanceCharge" valuePropName="checked" className="mb-3">
                  <Checkbox defaultChecked name="enableUserBalanceCharge" disabled={isForbidden}>
                    {t('Enable user balance charge')}
                  </Checkbox>
                </Form.Item>
                <Form.Item name="ignoreMinContractDuration" valuePropName="checked" className="mb-3">
                  <Checkbox defaultChecked name="ignoreMinContractDuration" disabled={isForbidden}>
                    {t('Ignore minimum rental period')}
                  </Checkbox>
                </Form.Item>
              </>
            )}
            <div className="flex justify-center">
              <Button theme="clear" onClick={toggleExtendedOptions} isDisabled={isForbidden}>
                {showExtendedOptions ? t('Hide extended options') : t('Show extended options')}
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeModal}>
              {t('Cancel', { ns: 'common' })}
            </Button>

            <Button type="submit" isLoading={isLoading}>
              {t('Close contract')}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
});
